import { useRef, useState } from 'react'
import { Upload, Info, Trash2, Loader2, AlertCircle, RefreshCw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { ATTACHMENT_ACCEPTED_MIME, ATTACHMENT_MAX_BYTES } from '@/types/cgt'
import { AttachmentPreview, formatBytes } from './AttachmentPreview'
import { AttachmentPreviewDialog } from './AttachmentPreviewDialog'
import type { FormAttachment } from '@/api/attachmentsApi'

interface Props {
  submissionId: string
  fieldId: string
  fieldLabel: string
  /** Existing attachment, when the field already has one. */
  attachment?: FormAttachment
  /** Blocks completion until a file is present — drives the red state. */
  required: boolean
  locked: boolean
  /** True while this field's upload/remove mutation is in flight. */
  busy: boolean
  /** Set when the completion attempt flagged this field's attachment as missing. */
  error?: string
  onUpload: (file: File) => void
  onRemove: () => void
  /**
   * Builder preview. Stays fully interactive so the author can see the control
   * work, but nothing uploads — there is no persisted submission behind it.
   */
  preview?: boolean
  /**
   * The file picked while in preview mode, and its setter. Lifted up to
   * FormRenderer (rather than kept as local state here) so its own required-
   * attachment completion check — which reads a map FormRenderer owns, not
   * anything internal to this component — can see a preview selection. Ignored
   * outside preview mode.
   */
  previewFile?: { fileName: string; sizeBytes: number } | null
  onPreviewFileChange?: (file: { fileName: string; sizeBytes: number } | null) => void
}

/**
 * The attachment control: a single upload ICON sitting next to its field, whose
 * ring carries the state (per spec 86eyfh0cc).
 *
 *   enabled + empty   → plain icon, no ring   ("you may attach something")
 *   required + empty  → RED ring              ("you must, and haven't")
 *   either + attached → GREEN ring            ("done")
 *
 * Deliberately not a labelled button: a form can have many attachable fields and
 * a row of "Attach file" buttons would out-shout the fields themselves. Clicking
 * an empty icon opens the picker; clicking a filled one opens a preview popover
 * with replace/remove.
 */
export function AttachmentControl({
  submissionId,
  fieldId,
  fieldLabel,
  attachment,
  required,
  locked,
  busy,
  error,
  onUpload,
  onRemove,
  preview = false,
  previewFile = null,
  onPreviewFileChange,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [localError, setLocalError] = useState<string | null>(null)
  // The full-size preview panel. Lives here rather than in the popover so it
  // survives the popover closing — see where it is rendered.
  const [viewerOpen, setViewerOpen] = useState(false)

  const stored = attachment ?? previewFile
  const satisfied = !!stored
  const message = error ?? localError

  const pick = (file: File | undefined) => {
    if (!file) return
    setLocalError(null)
    if (!ATTACHMENT_ACCEPTED_MIME.includes(file.type)) {
      setLocalError('Only JPEG, PNG and PDF files can be attached.')
      return
    }
    if (file.size > ATTACHMENT_MAX_BYTES) {
      setLocalError(
        `File is ${formatBytes(file.size)} — the limit is ${formatBytes(ATTACHMENT_MAX_BYTES)}.`,
      )
      return
    }
    if (preview) {
      onPreviewFileChange?.({ fileName: file.name, sizeBytes: file.size })
      return
    }
    onUpload(file)
  }

  const ring = satisfied
    ? 'border-green-600 text-green-600 hover:bg-green-600/10'
    : required || message
      ? 'border-destructive text-destructive hover:bg-destructive/10'
      : 'border-transparent text-primary hover:bg-primary/10'

  const title = satisfied
    ? `${stored!.fileName} attached to ${fieldLabel}`
    : required
      ? `A file attachment is required for ${fieldLabel}`
      : `Attach a file to ${fieldLabel}`

  const spinner = <Loader2 className='h-4 w-4 animate-spin' />
  // Upload glyph while empty ("click to attach"); Info once a file is present
  // ("click to view what's here") — reusing Upload for the attached state read
  // as "click to upload" even though it now opens a read-only preview.
  const pickerIcon = busy ? spinner : <Upload className='h-4 w-4' />
  const attachedIcon = busy ? spinner : <Info className='h-4 w-4' />

  const fileInput = (
    <input
      ref={inputRef}
      type='file'
      className='hidden'
      accept={ATTACHMENT_ACCEPTED_MIME.join(',')}
      onChange={e => {
        pick(e.target.files?.[0])
        // Reset so re-picking the SAME file still fires a change event.
        e.target.value = ''
      }}
    />
  )

  // Empty: the icon IS the picker.
  if (!satisfied) {
    return (
      <div className='flex shrink-0 flex-col items-end gap-0.5'>
        {fileInput}
        <button
          type='button'
          className={cn(
            'flex h-7 w-7 items-center justify-center rounded-full border-2 transition-colors',
            ring,
            locked && 'cursor-not-allowed opacity-40',
          )}
          aria-label={title}
          title={title}
          disabled={locked || busy}
          onClick={() => inputRef.current?.click()}
        >
          {pickerIcon}
        </button>
        {message && (
          <p className='flex max-w-[14rem] items-center gap-1 text-right text-[11px] text-destructive'>
            <AlertCircle className='h-3 w-3 shrink-0' />
            {message}
          </p>
        )}
      </div>
    )
  }

  // Attached: green icon opening a preview popover.
  return (
    <div className='flex shrink-0 flex-col items-end gap-0.5'>
      {fileInput}
      {/*
        The full-size viewer is a SIBLING of the popover, not a child of it.
        Mounted inside, it would be unmounted the instant the popover closed
        behind it — which is every time, since opening a dialog moves focus out.
      */}
      {attachment && (
        <AttachmentPreviewDialog
          submissionId={submissionId}
          fieldId={fieldId}
          fileName={attachment.fileName}
          sizeBytes={attachment.sizeBytes}
          open={viewerOpen}
          onOpenChange={setViewerOpen}
        />
      )}
      <Popover>
        <PopoverTrigger asChild>
          <button
            type='button'
            className={cn(
              'flex h-7 w-7 items-center justify-center rounded-full border-2 transition-colors',
              ring,
            )}
            aria-label={title}
            title={title}
          >
            {attachedIcon}
          </button>
        </PopoverTrigger>
        <PopoverContent align='end' className='w-64'>
          {preview ? (
            <div className='space-y-2'>
              <p className='truncate text-xs font-medium'>{stored!.fileName}</p>
              <p className='text-[11px] text-muted-foreground'>
                {formatBytes(stored!.sizeBytes)} — not uploaded in preview.
              </p>
            </div>
          ) : (
            <AttachmentPreview
              submissionId={submissionId}
              fieldId={fieldId}
              attachment={attachment!}
              onPreview={() => setViewerOpen(true)}
            />
          )}
          {!locked && (
            <div className='mt-2 flex gap-1.5 border-t pt-2'>
              <Button
                type='button'
                size='sm'
                variant='outline'
                className='h-7 flex-1 gap-1 text-xs'
                disabled={busy}
                onClick={() => inputRef.current?.click()}
              >
                <RefreshCw className='h-3 w-3' />
                Replace
              </Button>
              <Button
                type='button'
                size='sm'
                variant='outline'
                className='h-7 gap-1 text-xs text-muted-foreground hover:text-destructive'
                disabled={busy}
                onClick={() => (preview ? onPreviewFileChange?.(null) : onRemove())}
              >
                <Trash2 className='h-3 w-3' />
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  )
}
